import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceArea,
} from "recharts";

const COLORS = {
    rty: "rgba(220, 200, 130, 0.70)",     // pale yellow (Russell stands out)
    spx: "rgba(100, 170, 130, 0.65)",     // green (muted)
    ndx: "rgba(130, 170, 210, 0.60)",     // light blue (muted)
};

const SP500_EVENTS = [
    { start: "2026-01-01", end: "2026-01-15", label: "New Year" },
];

export default function IndexPerformance({ api, section, expanded }) {
    const [rawData, setRawData] = useState(null);
    const [tab, setTab] = useState("1M");
    const [loading, setLoading] = useState(true);

    const isRussell = section === "russell";
    const title = "Mega-Cap. Large-Cap. Small-Cap. 1M and YTD.";

    useEffect(() => {
        let alive = true;
        axios.get(`${api}/indexes/daily`)
            .then((r) => { if (alive) { setRawData(r.data); setLoading(false); } })
            .catch((err) => { 
                if (alive) {
                    console.error("IndexPerformance load error:", err?.message);
                    setLoading(false); 
                }
            });
        return () => { alive = false; };
    }, [api]);

    const latestDate = rawData?.latest_date;

    const chartData = useMemo(() => {
        if (!rawData?.series?.length) return [];
        const series = rawData.series;

        let startIdx = 0;
        if (tab === "1M") {
            const cutoff = new Date(series[series.length - 1].date);
            cutoff.setDate(cutoff.getDate() - 30);
            const cutStr = cutoff.toISOString().slice(0, 10);
            startIdx = series.findIndex((d) => d.date >= cutStr);
            if (startIdx < 0) startIdx = 0;
        }

        const window = series.slice(startIdx);
        if (window.length === 0) return [];

        const baseRty = window[0].rty;
        const baseSpx = window[0].spx;
        const baseNdx = window[0].ndx;

        return window.map((d) => ({
            date: d.date,
            t: new Date(d.date).getTime(),
            rty: baseRty ? ((d.rty / baseRty - 1) * 100) : null,
            spx: baseSpx ? ((d.spx / baseSpx - 1) * 100) : null,
            ndx: baseNdx ? ((d.ndx / baseNdx - 1) * 100) : null,
        }));
    }, [rawData, tab]);

    const latest = chartData.length > 0 ? chartData[chartData.length - 1] : null;
    const chartHeight = expanded ? 500 : 260;

    if (loading) {
        return (
            <div className="pord-body py-12 text-center" style={{ fontSize: 13 }}>
                loading index data…
            </div>
        );
    }

    return (
        <div data-testid={`index-perf-${section}`}>
            <h3 className="pord-heading text-xl sm:text-2xl text-white mb-1">{title}</h3>
            <p className="pord-body text-[12px] mb-2 max-w-xl" style={{ lineHeight: 1.5 }}>
                This chart illustrates how pricing has evolved across large-cap, mega-cap, and small-cap equities, comparing both 1-month and year-to-date performance. It highlights where narrow mega-cap leadership contrasts with, or gives way to, broader large-cap and small-cap moves over these windows.
            </p>
            <p className="pord-label mb-4" style={{ fontSize: 10 }}>
                As of {latestDate || "\u2014"} · Update EOD
            </p>

            {/* Tabs */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {["1M", "YTD"].map((t) => (
                    <button
                        key={t}
                        data-testid={`tab-${t}-${section}`}
                        onClick={() => setTab(t)}
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 11,
                            fontWeight: 400,
                            textTransform: "uppercase",
                            letterSpacing: "0.12em",
                            padding: "6px 16px",
                            borderRadius: 999,
                            border: tab === t ? "1px solid var(--pord-teal)" : "1px solid rgba(184,212,208,0.18)",
                            background: tab === t ? "rgba(184,212,208,0.08)" : "transparent",
                            color: tab === t ? "var(--pord-teal)" : "rgba(255,255,255,0.5)",
                            cursor: "pointer",
                            transition: "all 200ms ease",
                        }}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {/* Latest values */}
            {latest && (
                <div className="flex gap-5 mb-3 text-xs font-light tabular-nums">
                    <span style={{ color: "#dcc882" }}>RTY {fmtPct(latest.rty)}</span>
                    <span style={{ color: "#82aad2" }}>NDX {fmtPct(latest.ndx)}</span>
                    <span style={{ color: "#64aa82" }}>SPX {fmtPct(latest.spx)}</span>
                </div>
            )}

            <div style={{ width: "100%", height: chartHeight }}>
                {chartData.length === 0 ? (
                    <div className="pord-body h-full flex items-center justify-center">no data</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -6 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis
                                dataKey="t"
                                type="number"
                                scale="time"
                                domain={["dataMin", "dataMax"]}
                                tickFormatter={(t) =>
                                    new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                                }
                                tickLine={false}
                                axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                                minTickGap={50}
                            />
                            <YAxis
                                tickFormatter={(v) => `${v.toFixed(1)}%`}
                                tickLine={false}
                                axisLine={false}
                                width={50}
                            />
                            <Tooltip content={<PerfTooltip />} />

                            <Line type="monotone" dataKey="rty" stroke={COLORS.rty} strokeWidth={1.5} dot={false} name="Russell 2000" connectNulls />
                            <Line type="monotone" dataKey="ndx" stroke={COLORS.ndx} strokeWidth={1.5} dot={false} name="Nasdaq 100" connectNulls />
                            <Line type="monotone" dataKey="spx" stroke={COLORS.spx} strokeWidth={1.5} dot={false} name="S&P 500" connectNulls />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            {expanded && (
                <div className="mt-4">
                    <div className="flex flex-wrap gap-5 text-xs font-light">
                        <span style={{ color: "#dcc882" }}><span style={{ display: "inline-block", width: 14, height: 2, background: "#dcc882", marginRight: 6, verticalAlign: "middle" }} />Russell 2000 (RTY)</span>
                        <span style={{ color: "#82aad2" }}><span style={{ display: "inline-block", width: 14, height: 2, background: "#82aad2", marginRight: 6, verticalAlign: "middle" }} />Nasdaq 100 (NDX)</span>
                        <span style={{ color: "#64aa82" }}><span style={{ display: "inline-block", width: 14, height: 2, background: "#64aa82", marginRight: 6, verticalAlign: "middle" }} />S&P 500 (SPX)</span>
                    </div>
                </div>
            )}

            <div className="mt-3" style={{ fontSize: 10, letterSpacing: "0.08em" }}>
                <span style={{ color: "var(--pord-muted)" }}>Source: </span>
                <a
                    href="https://stockanalysis.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="source-stockanalysis-0202"
                    style={{ color: "var(--pord-sage)", textDecoration: "none", borderBottom: "1px solid rgba(154,173,168,0.3)" }}
                >
                    StockAnalysis
                </a>
            </div>
        </div>
    );
}

function fmtPct(v) {
    if (v == null) return "—";
    const sign = v > 0 ? "+" : "";
    return `${sign}${v.toFixed(2)}%`;
}

function PerfTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
        <div className="pord-surface px-4 py-3 text-xs" style={{ background: "rgba(26,29,18,0.92)" }}>
            <div className="pord-label mb-1">{p.date}</div>
            {p.rty != null && <div style={{ color: "#dcc882" }}>RTY: {fmtPct(p.rty)}</div>}
            {p.ndx != null && <div style={{ color: "#82aad2" }}>NDX: {fmtPct(p.ndx)}</div>}
            {p.spx != null && <div style={{ color: "#64aa82" }}>SPX: {fmtPct(p.spx)}</div>}
        </div>
    );
}
